import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import SEO, { BreadcrumbSchema } from "../components/SEO";
import { ChevronDown } from "lucide-react";

const FAQS = [
  { q: "What areas do you serve?", a: "Our residential work covers the greater Tampa Bay region and the Gulf Coast through Sarasota. We take commercial work across Florida, and our federal, disaster recovery and expert witness services extend nationwide." },
  { q: "Are you licensed and insured?", a: "Yes. BCPeabody is a Florida Certified General Contractor (CGC1515086) and is fully insured. Certificates are available on request." },
  { q: "Do I need an architect before contacting you?", a: "No. We work with homeowners at every stage, from an early idea with no drawings to a fully designed project ready for pricing. If you need an architect or designer, we can recommend one, and design-build is available." },
  { q: "Do you take on smaller renovations?", a: "Yes. We take on projects from kitchen and bath remodels to full custom homes. In our first conversation we'll tell you honestly whether we're the right fit." },
  { q: "How does pricing work?", a: "After a conversation and a site visit, we provide a line-item proposal with allowances shown separately, so you can see where every dollar goes before work starts." },
  { q: "How involved will I be during construction?", a: "As involved as you want to be. Some clients want weekly meetings, others want a short update when something matters. We set that up with you at the start." },
  { q: "What makes BCPeabody different?", a: "Federal construction discipline applied to residential work. Our founder ran the Corps of Engineers' Jacksonville District, and our team has delivered federal projects for the Corps and the VA. On your project that shows up as tighter planning, honest budgets and clean documentation." },
  { q: "Are you a veteran-owned business?", a: "Yes. BCPeabody is a Service-Disabled Veteran-Owned Small Business, owned and led by Col. (Ret.) Bob Carpenter, P.E." },
  { q: "Do you provide FEMA Public Assistance consulting?", a: "Yes. We support public agencies, nonprofits and recovery firms with project formulation, cost estimating, procurement compliance and hazard mitigation. We have managed more than 150 FEMA Public Assistance grants." },
  { q: "Can you serve as an expert witness in construction disputes?", a: "Yes. Col. (Ret.) Robert M. Carpenter, P.E., provides expert analysis and testimony in construction, federal contracting, FEMA, USACE and disaster recovery disputes. He is a licensed Professional Engineer in Florida, Virginia and Puerto Rico." },
  { q: "How do I start a project?", a: "Call (813) 919-5548 or use our contact form. The first conversation is free and there's no obligation." },
];

function FaqPageSchema() {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
}

export default function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <>
      <SEO
        title="FAQ | Tampa General Contractor"
        description="Answers about service areas, licensing, pricing, design-build, FEMA consulting and expert witness services from BCPeabody in Tampa."
        path="/faq"
      />
      <FaqPageSchema />
      <BreadcrumbSchema items={[{ name: "Home", url: "https://bcpeabody.com/" }, { name: "FAQ", url: "https://bcpeabody.com/faq" }]} />
      <Nav />

      <section className="pt-32 pb-10 bg-cream-light">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <div className="text-[11px] tracking-[0.18em] uppercase mb-5 text-copper">FAQ</div>
          <h1 className="font-serif font-medium text-ink tracking-tight leading-[1.05]" style={{ fontSize: "clamp(34px, 5vw, 60px)" }}>
            Frequently Asked Questions
          </h1>
        </div>
      </section>

      <section data-testid="faq-list" className="bg-cream-light pb-24">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 divide-y divide-ink/15 border-t border-ink/15">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={f.q} className="py-2">
                <button
                  data-testid={`faq-q-${i}`}
                  className="w-full flex items-start justify-between gap-6 py-5 text-left"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                >
                  <span className="font-serif text-xl md:text-2xl text-ink tracking-tight">{f.q}</span>
                  <ChevronDown size={22} className={`text-copper transition-transform shrink-0 mt-1 ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="pb-6 pr-8 text-ink/80 leading-relaxed" style={{ fontSize: 16 }}>
                    {f.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <Footer />
    </>
  );
}
